import {useEffect, useMemo, useState} from '@wordpress/element';

const toStr = (v) => (v == null ? '' : String(v));

const buildAttrMapFromItem = (it) => {
    const m = {};
    (it?.variations || []).forEach((v) => {
        if (v?.name) m[v.name] = toStr(v.value);
    });
    return m;
};

const deriveVariationData = (items) => {
    const catsSet = new Set();
    const options = new Map();
    const bySku = new Map();
    const attrBySku = new Map();

    items.forEach((it) => {
        const skuStr = toStr(it?.sku);
        if (!skuStr) return;

        bySku.set(skuStr, it);

        const attr = buildAttrMapFromItem(it);
        attrBySku.set(skuStr, attr);

        (it?.variations || []).forEach((v) => {
            const name = v?.name;
            const value = v?.value;
            if (!name) return;

            catsSet.add(name);
            if (!options.has(name)) options.set(name, new Set());
            if (value != null) options.get(name).add(toStr(value));
        });
    });

    const categories = Array.from(catsSet).filter((cat) => {
        const values = options.get(cat);
        return values && values.size > 1;
    });

    const optionsByCategory = categories.reduce((acc, cat) => {
        acc[cat] = Array.from(options.get(cat) || []);
        return acc;
    }, {});

    return {categories, optionsByCategory, itemsBySku: bySku, attrBySku};
};

const buildSelectionForItem = (it, categories, optionsByCategory, attrBySku) => {
    const skuStr = toStr(it?.sku);
    const attrs = attrBySku.get(skuStr) || {};
    const sel = {};

    categories.forEach((cat) => {
        if (attrs[cat] != null && attrs[cat] !== '') {
            sel[cat] = attrs[cat];
        } else if (optionsByCategory[cat]?.length) {
            sel[cat] = toStr(optionsByCategory[cat][0]);
        } else {
            sel[cat] = '';
        }
    });

    return sel;
};

const matchesSelectionStrict = (attrs, sel, categories) => {
    return categories.every((cat) => toStr(attrs?.[cat]) === toStr(sel?.[cat]));
};

const matchesConstraints = (attrs, constraints) => {
    for (const [cat, val] of Object.entries(constraints || {})) {
        const v = toStr(val);
        if (!v) continue;
        if (toStr(attrs?.[cat]) !== v) return false;
    }
    return true;
};

const findFirstItemMatchingConstraints = (items, attrBySku, constraints) => {
    for (const it of items) {
        const skuStr = toStr(it?.sku);
        const attrs = attrBySku.get(skuStr);
        if (!attrs) continue;
        if (matchesConstraints(attrs, constraints)) return it;
    }
    return null;
};

const existsAnyItemMatchingConstraints = (items, attrBySku, constraints) => {
    return !!findFirstItemMatchingConstraints(items, attrBySku, constraints);
};

const filterItemsByRequired = (items, attrBySku, requiredCat, requiredVal) => {
    const v = toStr(requiredVal);
    return items.filter((it) => {
        const skuStr = toStr(it?.sku);
        const attrs = attrBySku.get(skuStr);
        return attrs && toStr(attrs?.[requiredCat]) === v;
    });
};

const resolveClickRequiredFirst = ({
                                       items,
                                       categories,
                                       optionsByCategory,
                                       attrBySku,
                                       currentSelection,
                                       clickedCat,
                                       clickedValue,
                                   }) => {
    const requiredVal = toStr(clickedValue);

    const candidates = filterItemsByRequired(items, attrBySku, clickedCat, requiredVal);
    if (!candidates.length) return null;

    const perfectSelection = {...(currentSelection || {}), [clickedCat]: requiredVal};

    for (const it of candidates) {
        const skuStr = toStr(it?.sku);
        const attrs = attrBySku.get(skuStr);
        if (!attrs) continue;
        if (matchesSelectionStrict(attrs, perfectSelection, categories)) {
            return {
                item: it,
                selection: perfectSelection,
            };
        }
    }

    const constraints = {[clickedCat]: requiredVal};

    for (const cat of categories) {
        if (cat === clickedCat) continue;

        const opts = optionsByCategory?.[cat] || [];
        let chosen = '';

        for (const opt of opts) {
            const v = toStr(opt);
            if (existsAnyItemMatchingConstraints(candidates, attrBySku, {...constraints, [cat]: v})) {
                chosen = v;
                break;
            }
        }

        constraints[cat] = chosen;
    }

    const item = findFirstItemMatchingConstraints(candidates, attrBySku, constraints);
    if (!item) return null;

    {
        const skuStr = toStr(item?.sku);
        const attrs = attrBySku.get(skuStr);
        if (!attrs || toStr(attrs?.[clickedCat]) !== requiredVal) return null;
    }

    const selection = buildSelectionForItem(item, categories, optionsByCategory, attrBySku);
    selection[clickedCat] = requiredVal;

    return {item, selection};
};

const isOptionEnabled = (items, categories, attrBySku, selection, category, value) => {
    const testSel = {...(selection || {}), [category]: toStr(value)};

    for (const it of items) {
        const skuStr = toStr(it?.sku);
        const attrs = attrBySku.get(skuStr);
        if (!attrs) continue;

        let ok = true;
        for (const cat of categories) {
            const desired = toStr(testSel?.[cat]);
            if (!desired) continue;
            if (toStr(attrs?.[cat]) !== desired) {
                ok = false;
                break;
            }
        }
        if (ok) return true;
    }
    return false;
};

const getConflicts = (items, categories, attrBySku, selection, category, value) => {
    const v = toStr(value);

    const candidatesAttrs = [];
    for (const it of items) {
        const skuStr = toStr(it?.sku);
        const attrs = attrBySku.get(skuStr);
        if (!attrs) continue;
        if (toStr(attrs?.[category]) === v) candidatesAttrs.push(attrs);
    }

    if (!candidatesAttrs.length) return ['This option does not exist for this product'];

    const conflicts = [];
    for (const cat of categories) {
        if (cat === category) continue;

        const desired = toStr(selection?.[cat]);
        if (!desired) continue;

        const anyMatch = candidatesAttrs.some((attrs) => toStr(attrs?.[cat]) === desired);
        if (!anyMatch) conflicts.push(`${cat}: ${desired}`);
    }

    return conflicts.length ? conflicts : ['Not compatible with the current selection'];
};

const getTooltipText = (items, categories, attrBySku, selection, category, value) => {
    const conflicts = getConflicts(items, categories, attrBySku, selection, category, value);
    if (conflicts.length === 1 && conflicts[0].startsWith('This option')) return conflicts[0];

    return `Not available with: ${conflicts.join(', ')}. Click to switch to a compatible SKU.`;
};

const navigateToSlug = (slug) => {
    window.location.href = `/product/${slug}`;
};

export const VariationsSelector = ({variations = [], currentSku}) => {
    const items = Array.isArray(variations) ? variations.filter(Boolean) : [];

    const {categories, optionsByCategory, itemsBySku, attrBySku} = useMemo(() => {
        return deriveVariationData(items);
    }, [items]);

    const currentItem = itemsBySku.get(toStr(currentSku)) || items[0];

    const initialSelection = useMemo(() => {
        if (!currentItem) return {};
        return buildSelectionForItem(currentItem, categories, optionsByCategory, attrBySku);
    }, [currentItem, categories, optionsByCategory, attrBySku]);

    const [selection, setSelection] = useState(initialSelection);

    useEffect(() => {
        setSelection(initialSelection);

    }, [currentSku, initialSelection]);

    const onSelectOption = (category, value) => {
        const resolved = resolveClickRequiredFirst({
            items,
            categories,
            optionsByCategory,
            attrBySku,
            currentSelection: selection,
            clickedCat: category,
            clickedValue: value,
        });

        if (!resolved) return;

        setSelection(resolved.selection);
        navigateToSlug(resolved.item.slug);
    };

    if (!items.length || !categories.length) return null;

    return (
        <div className="mt-2 space-y-4">
            {categories.map((cat) => (
                <div key={cat} className="form-control w-full">
                    <div className="label">
                        <span className="label-text font-medium">{cat}</span>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {(optionsByCategory[cat] || []).map((opt) => {
                            const isSelected = toStr(selection?.[cat]) === toStr(opt);
                            const enabled = isOptionEnabled(items, categories, attrBySku, selection, cat, opt);

                            const tooltipText = enabled
                                ? null
                                : getTooltipText(items, categories, attrBySku, selection, cat, opt);

                            const baseClasses = 'btn btn-sm rounded-full';
                            const stateClasses = isSelected
                                ? 'btn-primary'
                                : enabled
                                    ? 'btn-outline'
                                    : 'btn-outline btn-warning';

                            const button = (
                                <button
                                    key={opt}
                                    type="button"
                                    className={`${baseClasses} ${stateClasses}`}
                                    onClick={isSelected ? undefined : () => onSelectOption(cat, opt)}
                                >
                                    {opt}
                                </button>
                            );

                            if (!enabled) {
                                return (
                                    <div
                                        key={`${cat}:${opt}`}
                                        className="tooltip tooltip-top"
                                        data-tip={tooltipText}
                                    >
                                        {button}
                                    </div>
                                );
                            }

                            return button;
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
};